import React, { useRef } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import styles from "./ClearControls.module.scss";

export function ClearButton({ onClear, label = "Clear search", disabled = false }: {
  onClear: () => void; label?: string; disabled?: boolean;
}) {
  return <button type="button" className={styles.clearButton} aria-label={label} title={label} disabled={disabled}
    onMouseDown={(event) => event.preventDefault()}
    onClick={(event) => { event.stopPropagation(); onClear(); }}>
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
  </button>;
}

type SearchInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  onClear: () => void;
  clearLabel?: string;
};

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { onClear, clearLabel, onKeyDown, ...props }, forwardedRef,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const clear = () => { onClear(); inputRef.current?.focus(); };
  return <span className={styles.searchInput}>
    <input {...props} ref={(node) => {
      inputRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    }} onKeyDown={(event) => {
      onKeyDown?.(event);
      if (!event.defaultPrevented && event.key === "Escape" && props.value && !props.disabled && !props.readOnly) {
        event.preventDefault(); event.stopPropagation(); clear();
      }
    }} />
    {Boolean(props.value) && !props.readOnly && <ClearButton label={clearLabel} disabled={props.disabled} onClear={clear} />}
  </span>;
});

type SearchTextFieldProps = TextFieldProps & { onClear: () => void; clearLabel?: string };

export function SearchTextField({ onClear, clearLabel, InputProps, onKeyDown, ...props }: SearchTextFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const clear = () => { onClear(); inputRef.current?.focus(); };
  return <TextField {...props} inputRef={inputRef} onKeyDown={(event) => {
    onKeyDown?.(event);
    if (!event.defaultPrevented && event.key === "Escape" && props.value && !props.disabled && !InputProps?.readOnly) {
      event.preventDefault(); event.stopPropagation(); clear();
    }
  }} InputProps={{ ...InputProps, endAdornment: <>
    {Boolean(props.value) && !InputProps?.readOnly && <ClearButton label={clearLabel} disabled={props.disabled} onClear={clear} />}
    {InputProps?.endAdornment}
  </> }} />;
}

export function ClearFiltersButton({ onClear, disabled = false }: { onClear: () => void; disabled?: boolean }) {
  return <button type="button" className={styles.clearFiltersButton} onClick={onClear} disabled={disabled}>Clear filters</button>;
}

export function FilterTextField({ active, onClear, clearLabel = "Clear filter", InputProps, ...props }: TextFieldProps & {
  active: boolean; onClear: () => void; clearLabel?: string;
}) {
  return <TextField {...props} InputProps={{ ...InputProps, endAdornment: <>
    {active && <span style={{ display: "inline-flex", marginRight: props.select ? 22 : 0 }}>
      <ClearButton label={clearLabel} onClear={onClear} disabled={props.disabled} />
    </span>}
    {InputProps?.endAdornment}
  </> }} />;
}

export function ClearableFilter({ children, onClear, active, label }: {
  children: React.ReactNode; onClear: () => void; active: boolean; label: string;
}) {
  return <div className={styles.filterControl} data-active={active}>
    {children}
    {active && <ClearButton label={`Clear ${label} filter`} onClear={onClear} />}
  </div>;
}
